import { ArrowRightIcon } from 'lucide-react'

import { cn } from 'src/shared/utils/cn'

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '.'

interface IconDropdownItem {
  label: string
  subLabel?: string
  icon?: React.ReactNode
  onClick?: () => void
}

interface IconDropdownProps {
  trigger: React.ReactNode
  /** A grid of items. Each array represents a column of items. */
  itemGrid: IconDropdownItem[][]
  /** Popup alignment relative to the trigger. Default is 'end'. */
  alignContent?: 'start' | 'center' | 'end'
  onOpenChange?: (open: boolean) => void
  /** A custom component to be rendered on the right side of the dropdown content. */
  customRightComponent?: React.ReactNode
  dropdownItemClassName?: string
}

export default function IconDropdown({
  trigger,
  itemGrid,
  alignContent = 'end',
  onOpenChange,
  customRightComponent,
  dropdownItemClassName,
}: IconDropdownProps) {
  return (
    <DropdownMenu modal={false} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild className="focus-visible:outline-none">
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={alignContent}
        className="flex flex-row gap-2 rounded-[12px] border-[0.5px] border-[#EFEFEF] bg-white p-2 shadow-sm"
      >
        {itemGrid.map((column, index) => (
          <div key={index} className="flex flex-col gap-1">
            {column.map((item) => (
              <DropdownMenuItem
                key={item.label}
                onClick={item.onClick}
                includeIconDefaultClasses={false}
                className={cn(
                  'group flex cursor-pointer flex-row rounded-[8px] px-[12px] py-[8px] hover:bg-[#f8f7f5]',
                  'text-[14px] leading-[1.70] text-primary',
                  'transition-all duration-150',
                  dropdownItemClassName,
                )}
              >
                {item.icon && (
                  <>
                    <div className="flex size-[40px] shrink-0 items-center justify-center rounded-[8px] bg-[#F8F7F5] group-hover:bg-[#45D88E]">
                      {item.icon}
                    </div>
                  </>
                )}
                <div className="flex flex-col">
                  <div className="flex flex-row items-center gap-1">
                    <span>{item.label}</span>
                    <ArrowRightIcon size={16} className="text-[#45D88E] opacity-0 group-hover:opacity-100" />
                  </div>
                  {item.subLabel && <span className="text-[13px] leading-[1.53] text-muted">{item.subLabel}</span>}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        ))}
        {customRightComponent && <div className="self-stretch">{customRightComponent}</div>}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
